import React, { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getFiscalYearData } from "../../../Redux/Actions/Apamanagement/FiscalYear/FiscalYearAction";
import axiosInstance from "../../../Services/Interceptor";
import CommonToastFunctions from "../../../Common/CommonFunctions/CommonToastFunctions";
import { convertNumber } from "../../../shade/convertNumber";
import CommonLoading from "../../../Common/CommonFunctions/CommonLoading";

const WorkplaceMasterDataAddUpdate = ({
  openModal,
  setOpenModal,
  fetchWorkplaceData,
  editData,
}) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = localStorage.getItem("i18nextLng");
  const fiscalYear = useSelector((state) => state?.fiscalYear?.fiscalYear);
  const [sectionLoading, setSectionLoading] = useState(false);
  const [formData, setFormData] = useState({
    nameBn: "",
    nameEn: "",
    fiscalYearId: "",
    minValue: "",
    maxValue: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editData) {
      setFormData(editData || {});
    }
  }, [editData]);

  const handleCloseModal = () => {
    setOpenModal(false);
    setErrors({});
  };

  const handleChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validate = () => {
    const temp = {};

    if (!formData.nameBn) temp.nameBn = true;
    if (!formData.nameEn) temp.nameEn = true;
    if (!formData.fiscalYearId) temp.fiscalYearId = true;
    if (!formData.minValue) temp.minValue = true;
    if (!formData.maxValue) temp.maxValue = true;

    if (
      formData.minValue &&
      formData.maxValue &&
      Number(formData.minValue) > Number(formData.maxValue)
    ) {
      temp.maxValue = "MAX_LT_MIN";
    }

    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      ...formData,
      fiscalYearId: Number(formData.fiscalYearId),
      maxValue: Number(formData.maxValue),
      minValue: Number(formData.minValue),
    };

    setSectionLoading(true);
    if (editData) {
      axiosInstance
        .put(
          `/apa-config/api/v1/strategic-objective-fy-mappings/${editData.id}`,
          payload
        )
        .then((res) => {
          if (res?.status === 200) {
            CommonToastFunctions("success", t("CommonToast.save"));
            fetchWorkplaceData();
            handleCloseModal();
          }
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          setSectionLoading(false);
        });
    } else {
      axiosInstance
        .post(
          `/apa-config/api/v1/strategic-objective-fy-mappings/store`,
          payload
        )
        .then((res) => {
          if (res?.status === 201) {
            CommonToastFunctions("success", t("CommonToast.save"));
            fetchWorkplaceData();
            handleCloseModal();
          }
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          setSectionLoading(false);
        });
    }
  };

  return (
    <Modal size="md" centered show={openModal} onHide={handleCloseModal}>
      <Modal.Header>
        <Modal.Title>{t("Others.workplaceBudgetInfo")}</Modal.Title>
        <Button
          variant="danger"
          className="btn-sm ms-auto"
          onClick={handleCloseModal}
        >
          <i className="fas fa-times" />
        </Button>
      </Modal.Header>
      {sectionLoading && <CommonLoading />}

      <Form onSubmit={onSubmit}>
        <Modal.Body>
          <Row>
            {/* Fiscal Year */}
            <Col md={12} className="mt-3">
              <Form.Label>
                {t("Common.fiscalYear")} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={formData.fiscalYearId}
                onChange={(e) => handleChange("fiscalYearId", e.target.value)}
              >
                <option value="">{t("CommonBtn.placeholder")}</option>
                {fiscalYear?.map((item) => (
                  <option key={item.id} value={item.id}>
                    {i18n.language === "en" ? item.titleEn : item.titleBn}
                  </option>
                ))}
              </Form.Select>
              {errors.fiscalYearId && (
                <small className="text-danger">
                  {t("validation.required")}
                </small>
              )}
            </Col>

            {/* Name Bangla */}
            <Col md={12} className="mt-2">
              <Form.Label>
                {t("menus.titleBn")} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                value={formData.nameBn}
                placeholder={t("CommonBtn.placeholder2")}
                onChange={(e) => handleChange("nameBn", e.target.value)}
              />
              {errors.nameBn && (
                <small className="text-danger">
                  {t("validation.required")}
                </small>
              )}
            </Col>

            {/* Name English */}
            <Col md={12} className="mt-2">
              <Form.Label>
                {t("menus.titleEn")} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                value={formData.nameEn}
                placeholder={t("CommonBtn.placeholder2")}
                onChange={(e) => handleChange("nameEn", e.target.value)}
              />
              {errors.nameEn && (
                <small className="text-danger">
                  {t("validation.required")}
                </small>
              )}
            </Col>

            {/* Min Value */}
            <Col md={6} className="mt-2">
              <Form.Label>
                {i18n.language === "en" ? "Minimum Limit" : "সর্বনিম্ন সীমা"}{" "}
                <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={convertNumber(formData.minValue, currentLanguage)}
                placeholder={t("CommonBtn.placeholder2")}
                onChange={(e) =>
                  handleChange(
                    "minValue",
                    e.target.value.replace(/[০-৯]/g, (d) =>
                      "০১২৩৪৫৬৭৮৯".indexOf(d)
                    )
                  )
                }
                onKeyDown={(e) => {
                  const allowedKeys = [
                    "Backspace",
                    "Delete",
                    "ArrowLeft",
                    "ArrowRight",
                    "Tab",
                    "Enter",
                  ];
                  if (allowedKeys.includes(e.key)) return;
                  if (
                    (e.key >= "0" && e.key <= "9") ||
                    (e.key >= "০" && e.key <= "৯")
                  )
                    return;
                  e.preventDefault();
                }}
              />
              {errors.minValue && (
                <small className="text-danger">
                  {t("validation.required")}
                </small>
              )}
            </Col>

            {/* Max Value */}
            <Col md={6} className="mt-2">
              <Form.Label>
                {i18n.language === "en" ? "Maximum Limit" : "সর্বোচ্চ সীমা"}{" "}
                <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={convertNumber(formData.maxValue, currentLanguage)}
                placeholder={t("CommonBtn.placeholder2")}
                onChange={(e) =>
                  handleChange(
                    "maxValue",
                    e.target.value.replace(/[০-৯]/g, (d) =>
                      "০১২৩৪৫৬৭৮৯".indexOf(d)
                    )
                  )
                }
                onKeyDown={(e) => {
                  const allowedKeys = [
                    "Backspace",
                    "Delete",
                    "ArrowLeft",
                    "ArrowRight",
                    "Tab",
                    "Enter",
                  ];
                  if (allowedKeys.includes(e.key)) return;
                  if (
                    (e.key >= "0" && e.key <= "9") ||
                    (e.key >= "০" && e.key <= "৯")
                  )
                    return;
                  e.preventDefault();
                }}
              />
              {errors.maxValue === true && (
                <small className="text-danger">
                  {t("validation.required")}
                </small>
              )}
              {errors.maxValue === "MAX_LT_MIN" && (
                <small className="text-danger">
                  Max value must be greater than Min value
                </small>
              )}
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button type="submit" variant="primary">
            {t("CommonBtn.create")}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default WorkplaceMasterDataAddUpdate;
